import type { GeneratedRoofFramingPlane } from "building-types/framing/roofFramingTypes";
import { intersectRay3PlaneWithNormal } from "geom/intersectRay3";
import type { Plane } from "geom/planeTypes";
import type { PolyLine3 } from "geom/polyLine3";
import { vec2Dist } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import { vec3Normalize, vec3Sub } from "geom/vec3";

interface HipSpanCalculationParams {
  readonly peakOrigin: {
    x: number;
    y: number;
    z: number;
  };
  readonly indexOrigin: {
    x: number;
    y: number;
  };
  readonly ridgeRail: PolyLine3;
  readonly trussPlane: Plane;
  readonly peakRailExtendedNorm: [number, number, number];
}

function calculateIntersectionRidgePlane(
  ridgeRail: PolyLine3,
  peakOrigin: { x: number; y: number; z: number },
  trussPlane: Plane,
  peakRailExtendedNorm: [number, number, number],
): Vec3 | undefined {
  const [ridgeRailX, ridgeRailY, ridgeRailZ] = vec3Sub(
    ridgeRail[0],
    ridgeRail[1],
    ridgeRail[2],
    ridgeRail[3],
    ridgeRail[4],
    ridgeRail[5],
  );

  const [ridgeRailNormX, ridgeRailNormY, ridgeRailNormZ] = vec3Normalize(ridgeRailX, ridgeRailY, ridgeRailZ);

  const intersectionVec = intersectRay3PlaneWithNormal(
    peakOrigin.x,
    peakOrigin.y,
    peakOrigin.z,
    ridgeRailNormX,
    ridgeRailNormY,
    ridgeRailNormZ,
    trussPlane.originX,
    trussPlane.originY,
    trussPlane.originZ,
    ...peakRailExtendedNorm,
  );

  return intersectionVec ? [intersectionVec[0], intersectionVec[1], intersectionVec[2]] : undefined;
}

export function calculateHipSpan(params: HipSpanCalculationParams): {
  span: number | undefined;
  intersectionVec3: Vec3 | undefined;
} {
  const { peakOrigin, indexOrigin, ridgeRail, trussPlane, peakRailExtendedNorm } = params;

  const intersectionVec = calculateIntersectionRidgePlane(ridgeRail, peakOrigin, trussPlane, peakRailExtendedNorm);

  if (!intersectionVec) {
    return { span: undefined, intersectionVec3: undefined };
  }

  const [intersectionX, intersectionY, intersectionZ] = [intersectionVec[0], intersectionVec[1], intersectionVec[2]];
  return {
    span: vec2Dist(intersectionX, intersectionY, indexOrigin.x, indexOrigin.y),
    intersectionVec3: [intersectionX, intersectionY, intersectionZ],
  };
}

export function calculateHipSpans(
  index: number,
  framingPlanes: GeneratedRoofFramingPlane[],
  peakFramingPlaneIndex: number,
  leftRidgeRail: PolyLine3,
  rightRidgeRail: PolyLine3,
  peakRailExtendedNorm: [number, number, number],
): { leftHipSpan: number | undefined; rightHipSpan: number | undefined } {
  if (peakFramingPlaneIndex === undefined || !leftRidgeRail || !rightRidgeRail) {
    return { leftHipSpan: undefined, rightHipSpan: undefined };
  }

  const { originX: indexOriginX, originY: indexOriginY } = framingPlanes[index].plane;
  const {
    originX: peakOriginX,
    originY: peakOriginY,
    originZ: peakOriginZ,
  } = framingPlanes[peakFramingPlaneIndex].plane;

  const commonParams = {
    peakOrigin: { x: peakOriginX, y: peakOriginY, z: peakOriginZ },
    indexOrigin: { x: indexOriginX, y: indexOriginY },
    trussPlane: framingPlanes[index].plane,
    peakRailExtendedNorm,
  };

  const leftHipSpan = calculateHipSpan({
    ...commonParams,
    ridgeRail: leftRidgeRail,
  });

  const rightHipSpan = calculateHipSpan({
    ...commonParams,
    ridgeRail: rightRidgeRail,
  });

  return { leftHipSpan: leftHipSpan.span, rightHipSpan: rightHipSpan.span };
}
